import { SessionData, AnalysisResult } from '../types';
import { calculateFVProfile, calculateTrainingZones, checkDataQuality } from './fv';
import { classifyAthlete } from './classify';
import { analyzeSplits } from './splits';
import { 
  calculateVelocityLossMetrics, 
  calculateLoadVelocityMetrics, 
  calculatePowerDistribution, 
  calculateMechanicalEfficiency, 
  calculateTrainingReadiness, 
  calculatePerformanceRatios, 
  calculateDistanceMetrics,
  calculateValidationMetrics,
  calculateMechanicalAnalysis,
  calculateFatigueMetrics,
  calculateAdvancedProfiling,
  calculateInjuryPreventionIndicators
} from './additionalKPIs';

export function analyzeSession(data: SessionData): AnalysisResult {
  const { athlete, session, refs } = data;
  
  // Calculate F-V profile
  const fvProfile = calculateFVProfile(session.trials, athlete.massKg, session.v_free_ms);
  
  // Calculate training zones
  const zones = calculateTrainingZones(fvProfile, session.v_free_ms);
  
  // Classify athlete
  const classification = classifyAthlete(fvProfile, refs);
  
  // Check data quality
  const quality = checkDataQuality(session.trials, fvProfile, session.v_free_ms);
  
  // Analyze splits if available
  let splits;
  if (session.splits && session.splits.dist_m.length > 3) {
    try {
      splits = analyzeSplits(session.splits.dist_m, session.splits.time_s);
    } catch (error) {
      console.warn('Split analysis failed:', error);
    }
  }
  
  // Calculate additional KPIs
  const velocityLoss = calculateVelocityLossMetrics(session.trials, session.v_free_ms);
  const loadVelocity = calculateLoadVelocityMetrics(fvProfile, session.v_free_ms);
  const powerDistribution = calculatePowerDistribution(session.trials);
  const mechanicalEfficiency = calculateMechanicalEfficiency(session.trials, fvProfile, session.v_free_ms, athlete.massKg);
  const trainingReadiness = calculateTrainingReadiness(fvProfile, session.v_free_ms, athlete.sport);
  const performanceRatios = calculatePerformanceRatios(fvProfile, session.v_free_ms, athlete.massKg);
  const distanceMetrics = calculateDistanceMetrics(session.v_free_ms, fvProfile, session.sprint_distance_m);
  
  // Calculate enhanced metrics if power/time data available
  const validation = calculateValidationMetrics(session.trials, session.sprint_distance_m);
  const mechanicalAnalysis = calculateMechanicalAnalysis(session.trials, fvProfile);
  const fatigueMetrics = calculateFatigueMetrics(session.trials);
  const advancedProfiling = calculateAdvancedProfiling(session.trials, session.sprint_distance_m);
  const injuryPrevention = calculateInjuryPreventionIndicators(session.trials, fvProfile, velocityLoss);
  
  return {
    fvProfile,
    zones,
    classification,
    splits,
    quality,
    velocityLoss,
    loadVelocity,
    powerDistribution,
    mechanicalEfficiency,
    trainingReadiness,
    performanceRatios,
    distanceMetrics,
    validation,
    mechanicalAnalysis,
    fatigueMetrics,
    advancedProfiling,
    injuryPrevention
  };
}

export function generateSampleData(): SessionData {
  return {
    athlete: {
      name: "Max Johnson",
      massKg: 78.5,
      sport: "Football",
      position: "WR"
    },
    session: {
      date: "2025-01-27",
      v_free_ms: 9.1,
      sprint_distance_m: 30,
      trials: [
        { load_kgf: 5, v_max_ms: 7.8, power_W: 1200, sprint_time_s: 3.85 },
        { load_kgf: 10, v_max_ms: 7.0, power_W: 1350, sprint_time_s: 4.29 },
        { load_kgf: 20, v_max_ms: 5.6, power_W: 1400, sprint_time_s: 5.36 },
        { load_kgf: 30, v_max_ms: 4.4, power_W: 1320, sprint_time_s: 6.82 }
      ],
      splits: {
        dist_m: [0, 5, 10, 20, 30],
        time_s: [0, 1.05, 1.80, 3.25, 4.35]
      }
    },
    refs: {
      sport: "General",
      F0_ref_Nkg: 7.5,
      V0_ref_ms: 9.5
    }
  };
}